/**
 * Timothy Graham Portfolio - Main Application
 * Modernized TypeScript application for premium portfolio
 * @version 3.0.0
 */

import { BlogEngine } from './blog-engine';
import { TSDemo } from './components/ts-demo';

// ============================================================================
// Core Application Class
// ============================================================================

class PortfolioApp {
  private theme: 'light' | 'dark' = 'dark';
  private navbar: HTMLElement | null = null;
  private navMenu: HTMLElement | null = null;
  private themeToggle: HTMLElement | null = null;

  constructor() {
    this.init();
  }

  private init(): void {
    this.navbar = document.getElementById('navbar');
    this.navMenu = document.getElementById('nav-menu');
    this.themeToggle = document.getElementById('theme-toggle');

    this.initTheme();
    this.initNavigation();
    this.initMobileMenu();
    this.initScrollAnimations();
    this.initCanvasBackground();
    this.initBlog();
    this.initTSDemo();
    this.initTypewriter();
    this.initMouseParallax();
    
    console.log("🚀 Portfolio Engine Initialized");
  }

  private initBlog(): void {
    const blog = new BlogEngine('blog-posts');
    blog.init();
  }

  private initTSDemo(): void {
    const demo = new TSDemo('.hero__code-window');
    demo.init();
  }

  private initTypewriter(): void {
    const textElement = document.getElementById('typewriter-text');
    if (!textElement) return;

    const words = ['TypeScript', 'C#', 'Python', 'Rust', 'Java'];
    let wordIndex = 0;
    let charIndex = words[0].length; // Starts fully typed
    let isDeleting = true; // We start by deleting the initial word

    const type = () => {
      const currentWord = words[wordIndex];
      
      if (isDeleting) {
        charIndex--;
      } else {
        charIndex++;
      }

      textElement.textContent = currentWord.substring(0, charIndex);

      // Typing speed: deleting is faster than typing
      let typeSpeed = isDeleting ? 60 : 120;

      if (!isDeleting && charIndex === currentWord.length) {
        // Finished typing the word, pause before deleting
        typeSpeed = 3000;
        isDeleting = true;
      } else if (isDeleting && charIndex === 0) {
        // Finished deleting the word, move to next word and pause briefly
        isDeleting = false;
        wordIndex = (wordIndex + 1) % words.length;
        typeSpeed = 500;
      }

      setTimeout(type, typeSpeed);
    };

    // Wait 3 seconds before starting the first deletion
    setTimeout(type, 3000);
  }

  private initTheme(): void {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    this.theme = savedTheme || (prefersDark ? 'dark' : 'light');
    this.setTheme(this.theme);

    this.themeToggle?.addEventListener('click', () => {
      this.toggleThemeWithAnimation();
    });
  }

  private toggleThemeWithAnimation(): void {
    const shard = document.getElementById('theme-shard');
    if (!shard) return;

    shard.classList.add('animating');
    
    setTimeout(() => {
      this.theme = this.theme === 'dark' ? 'light' : 'dark';
      this.setTheme(this.theme);
      localStorage.setItem('theme', this.theme);
    }, 400);

    setTimeout(() => {
      shard.classList.remove('animating');
    }, 800);
  }

  private initMobileMenu(): void {
    const toggle = document.getElementById('mobile-menu-toggle');
    if (!toggle || !this.navMenu) return;

    toggle.addEventListener('click', () => {
      this.navMenu?.classList.toggle('navbar__menu--active');
      toggle.classList.toggle('navbar__toggle--active');
    });

    // Close mobile menu when a link is clicked
    const links = this.navMenu.querySelectorAll('.navbar__link');
    links.forEach(link => {
      link.addEventListener('click', () => {
        this.navMenu?.classList.remove('navbar__menu--active');
        toggle.classList.remove('navbar__toggle--active');
      });
    });
  }

  private setTheme(theme: 'light' | 'dark'): void {
    document.documentElement.setAttribute('data-theme', theme);
  }

  private initNavigation(): void {
    if (!this.navbar) return;

    window.addEventListener('scroll', () => {
      if (window.scrollY > 50) {
        this.navbar?.classList.add('navbar--scrolled');
      } else {
        this.navbar?.classList.remove('navbar--scrolled');
      }
      this.updateActiveLink();
    }, { passive: true });
  }

  private updateActiveLink(): void {
    const sections = document.querySelectorAll('section[id]');
    const navLinks = document.querySelectorAll('.navbar__link');
    
    let currentId = '';
    sections.forEach(section => {
      const sectionTop = (section as HTMLElement).offsetTop;
      if (window.scrollY >= sectionTop - 100) {
        currentId = section.getAttribute('id') || '';
      }
    });

    navLinks.forEach(link => {
      link.classList.remove('navbar__link--active');
      if (link.getAttribute('href') === `#${currentId}`) {
        link.classList.add('navbar__link--active');
      }
    });
  }

  private initScrollAnimations(): void {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal--visible');
          // Don't unobserve if we want them to re-animate or keep reveal--visible
        }
      });
    }, { threshold: 0.15 });

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
  }

  private initMouseParallax(): void {
    const hero = document.getElementById('home');
    if (!hero) return;

    window.addEventListener('mousemove', (e) => {
      const mouseX = (e.clientX / window.innerWidth - 0.5) * 20;
      const mouseY = (e.clientY / window.innerHeight - 0.5) * 20;

      const elements = hero.querySelectorAll('.hero__text, .hero__visual');
      elements.forEach((el, index) => {
        const speed = (index + 1) * 0.5;
        (el as HTMLElement).style.transform = `translate(${mouseX * speed}px, ${mouseY * speed}px)`;
      });
    }, { passive: true });
  }

  private initCanvasBackground(): void {
    const canvas = document.createElement('canvas');
    const container = document.getElementById('hero-bg');
    if (!container) return;
    
    container.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize, { passive: true });
    resize();

    // Respect users who prefer reduced motion - render a single static frame.
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Neural Network / Constellation (Plexus) Effect - Full Page
    const nodes: any[] = [];
    const nodeCount = 150; // Increased for full page
    const connectionRadius = 180;
    const mouseRadius = 250;
    let mouseX = -1000;
    let mouseY = -1000;

    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.6,
        vy: (Math.random() - 0.5) * 0.6,
        size: Math.random() * 2.5 + 1.5 // Slightly larger nodes
      });
    }

    window.addEventListener('mousemove', (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    }, { passive: true });

    let rafId = 0;
    let running = false;

    const drawFrame = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const style = getComputedStyle(document.documentElement);
      const nodeColor = style.getPropertyValue('--network-node-color').trim();
      const lineColor = style.getPropertyValue('--network-line-color').trim();

      // Update and draw nodes
      nodes.forEach((node, i) => {
        // Subtle drift
        node.x += node.vx;
        node.y += node.vy;

        // Bounce off edges
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

        // Mouse interaction (subtle attraction)
        const dx = mouseX - node.x;
        const dy = mouseY - node.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < mouseRadius) {
          node.x += dx * 0.01;
          node.y += dy * 0.01;
        }

        // Draw connections
        for (let j = i + 1; j < nodes.length; j++) {
          const other = nodes[j];
          const dx2 = other.x - node.x;
          const dy2 = other.y - node.y;
          const dist2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

          if (dist2 < connectionRadius) {
            ctx.beginPath();
            ctx.strokeStyle = lineColor;
            ctx.globalAlpha = 1 - (dist2 / connectionRadius);
            ctx.lineWidth = 0.5;
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.stroke();
          }
        }

        // Draw node
        ctx.beginPath();
        ctx.fillStyle = nodeColor;
        ctx.globalAlpha = 1;
        ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.globalAlpha = 1; // Reset alpha
    };

    const animate = () => {
      drawFrame();
      rafId = requestAnimationFrame(animate);
    };

    const start = () => {
      if (running || prefersReducedMotion) return;
      running = true;
      animate();
    };

    const stop = () => {
      running = false;
      cancelAnimationFrame(rafId);
    };

    // Pause the animation when the tab is hidden or the hero has scrolled
    // out of view - no point burning CPU/battery on invisible pixels.
    let heroVisible = true;
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else if (heroVisible) start();
    });

    const observer = new IntersectionObserver(([entry]) => {
      heroVisible = entry.isIntersecting;
      if (heroVisible && !document.hidden) start();
      else stop();
    });
    observer.observe(container);

    if (prefersReducedMotion) drawFrame();
    else start();
  }
}

// Instantiate the application
document.addEventListener('DOMContentLoaded', () => {
  new PortfolioApp();
});

